import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ICONS, Glyph, type IconComponent } from '../ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { askJev } from '../jev/jevEvents';
import { searchEvents } from './searchEvents';
import { matchPages } from './searchCatalog';
import './commandPalette.css';

type RecordType = 'contact' | 'interactive' | 'segment' | 'sequence' | 'project';
interface RecordItem { type: RecordType; id: string; label: string; sublabel: string | null; href: string }
interface Option { key: string; group: string; label: string; sublabel?: string | null; glyph: string; run: () => void; recent?: { label: string; sublabel?: string | null; href: string; glyph: string } }

const TYPE: Record<RecordType, { group: string; glyph: string }> = {
  contact: { group: 'Contatos', glyph: 'Person' },
  interactive: { group: 'Campanhas interativas', glyph: 'Megaphone' },
  segment: { group: 'Segmentos', glyph: 'Filter' },
  sequence: { group: 'Sequências', glyph: 'Clock' },
  project: { group: 'Projetos', glyph: 'Folder' },
};

/**
 * Glifos usados pela paleta → nome oficial do Cloudscape. Os que não têm
 * entrada no mapa padrão (Filter, Folder) são resolvidos aqui.
 */
const GLYPH_ICONS: Record<string, IconComponent> = {
  Person: ICONS.Person,
  Megaphone: ICONS.Megaphone,
  Filter: ICONS.Filter,
  Clock: ICONS.Clock,
  Folder: ICONS.Folder,
  File: ICONS.File,
  ArrowRight: ICONS.ArrowRight,
  Sparkle: ICONS.Sparkle,
  MagnifyingGlass: ICONS.MagnifyingGlass,
};

const glyphIcon = (name: string): IconComponent => GLYPH_ICONS[name] ?? ICONS.File;

type Recent = { label: string; sublabel?: string | null; href: string; glyph: string };
const recentKey = (tenant: string | null | undefined) => `ingrave:busca:recentes:${tenant || '-'}`;
function readRecents(tenant: string | null | undefined): Recent[] {
  try { const v = JSON.parse(localStorage.getItem(recentKey(tenant)) || '[]'); return Array.isArray(v) ? v.slice(0, 6) : []; } catch { return []; }
}
function saveRecent(tenant: string | null | undefined, r: Recent) {
  try {
    const list = [r, ...readRecents(tenant).filter((x) => x.href !== r.href)].slice(0, 6);
    localStorage.setItem(recentKey(tenant), JSON.stringify(list));
  } catch { /* sem armazenamento: só não lembra */ }
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
export const shortcutLabel = isMac ? '⌘K' : 'Ctrl K';

/**
 * Busca global (Ctrl+K / ⌘K): registros da organização, telas e ações,
 * recentes e "Perguntar à Lumi". Navegação por teclado; tela cheia no celular.
 */
export function CommandPalette({ jevEnabled }: { jevEnabled: boolean }) {
  const navigate = useNavigate();
  const fetchApi = useAuthenticatedFetch();
  const { can, selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);

  const show = useCallback((text = '') => {
    lastFocus.current = document.activeElement as HTMLElement | null;
    setQ(text); setActive(0); setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);
  const close = useCallback(() => { setOpen(false); setRecords([]); setTimeout(() => lastFocus.current?.focus?.(), 0); }, []);

  // Ctrl+K / ⌘K abre (ou fecha); evento openSearch abre de qualquer tela
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); if (open) close(); else show(); }
    };
    const onOpen = (e: Event) => show((e as CustomEvent<string>).detail || '');
    window.addEventListener('keydown', onKey);
    searchEvents.addEventListener('open', onOpen);
    return () => { window.removeEventListener('keydown', onKey); searchEvents.removeEventListener('open', onOpen); };
  }, [open, show, close]);

  // Busca nos registros: 200 ms depois de parar de digitar; a anterior é cancelada
  useEffect(() => {
    if (!open) return;
    const text = q.trim();
    if (text.length < 2) { setRecords([]); setLoading(false); setFailed(false); return; }
    const ctrl = new AbortController();
    setLoading(true); setFailed(false);
    const t = setTimeout(async () => {
      try {
        const r = await fetchApi(`/api/search?q=${encodeURIComponent(text)}`, { signal: ctrl.signal });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || 'Erro na busca');
        setRecords(d.items || []); setActive(0);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') { setRecords([]); setFailed(true); }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 200);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open, fetchApi]);

  const go = useCallback((r: Recent) => { saveRecent(tenantId, r); close(); navigate(r.href); }, [tenantId, close, navigate]);

  const options: Option[] = useMemo(() => {
    const text = q.trim();
    const out: Option[] = [];
    if (!text) {
      for (const r of readRecents(tenantId)) out.push({ key: `recent:${r.href}`, group: 'Recentes', label: r.label, sublabel: r.sublabel, glyph: r.glyph, run: () => go(r) });
    }
    for (const it of records) {
      const meta = TYPE[it.type] ?? { group: 'Registros', glyph: 'File' };
      const rec = { label: it.label, sublabel: it.sublabel, href: it.href, glyph: meta.glyph };
      out.push({ key: `${it.type}:${it.id}`, group: meta.group, label: it.label, sublabel: it.sublabel, glyph: meta.glyph, run: () => go(rec) });
    }
    for (const p of matchPages(text, can)) {
      const rec = { label: p.label, sublabel: p.section, href: p.href, glyph: 'ArrowRight' };
      out.push({ key: `page:${p.id}`, group: 'Telas e ações', label: p.label, sublabel: p.section, glyph: 'ArrowRight', run: () => go(rec) });
    }
    if (jevEnabled && text) {
      out.push({ key: 'jev', group: 'Lumi', label: `Perguntar à Lumi: "${text}"`, glyph: 'Sparkle', run: () => { close(); askJev(text); } });
    }
    return out;
  }, [q, records, can, jevEnabled, tenantId, go, close]);

  useEffect(() => { if (active >= options.length) setActive(Math.max(0, options.length - 1)); }, [options.length, active]);
  useEffect(() => { listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (options.length ? (i + 1) % options.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (options.length ? (i - 1 + options.length) % options.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); options[active]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') { e.preventDefault(); }
  };

  if (!open) return null;
  const text = q.trim();
  let lastGroup = '';
  return createPortal(
    <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Busca na Ingrave">
        <div className="cmdk-input">
          <ICONS.MagnifyingGlass size={16} />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-autocomplete="list"
            aria-activedescendant={options[active] ? `cmdk-opt-${active}` : undefined}
            aria-label="Buscar contatos, campanhas, telas e ações"
            placeholder="Buscar contatos, campanhas, segmentos, telas…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span className="cmdk-spinner" role="status" aria-label="Buscando" />}
          <button type="button" className="cmdk-esc" onClick={close} aria-label="Fechar busca">Esc</button>
        </div>
        <div id="cmdk-list" ref={listRef} className="cmdk-list" role="listbox" aria-label="Resultados">
          {options.map((o, i) => {
            const header = o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            return (
              <div key={o.key} role="presentation">
                {header && <div className="cmdk-group" role="presentation">{header}</div>}
                <div
                  id={`cmdk-opt-${i}`}
                  data-idx={i}
                  role="option"
                  aria-selected={i === active}
                  className={`cmdk-option${i === active ? ' is-active' : ''}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => o.run()}
                >
                  <Glyph icon={glyphIcon(o.glyph)} size={16}/>
                  <span className="cmdk-option__text">
                    <span className="cmdk-option__label">{o.label}</span>
                    {o.sublabel && <span className="cmdk-option__sub">{o.sublabel}</span>}
                  </span>
                </div>
              </div>
            );
          })}
          {!options.length && (
            <div className="cmdk-empty" role="status">
              {!text ? 'Digite para buscar contatos, campanhas, segmentos, sequências, projetos e telas.'
                : loading ? 'Buscando…'
                  : failed ? 'Não foi possível buscar agora. Tente de novo.'
                    : text.length < 2 ? 'Digite pelo menos 2 caracteres.'
                      : `Nada encontrado para "${text}".`}
            </div>
          )}
        </div>
        <div className="cmdk-footer" aria-hidden>
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span><span><kbd>Enter</kbd> abrir</span><span><kbd>Esc</kbd> fechar</span><span className="cmdk-footer__right">{shortcutLabel} de qualquer tela</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
